import { useEffect, useRef } from "react";

import { useQueryParams } from "./useQueryParams";

type UseQueryParamsNonNumericTypes = string | boolean | undefined | object;

/**
 * Wraps useQueryParams with localStorage persistence.
 * On mount, if no URL params exist, restores from localStorage (or falls back to provided defaults).
 * Persists filters to localStorage whenever they change.
 *
 * @param storageKey - localStorage key for persisting filters
 * @param defaultFilters - fallback filters when nothing is saved
 */
export function usePersistedFilters<T extends Record<string, UseQueryParamsNonNumericTypes>>(
  storageKey: string,
  defaultFilters?: Partial<T>
) {
  const { filters, setFilters, navigateWithFilters } = useQueryParams<T>();
  const hasInitialized = useRef(false);
  const prevStorageKey = useRef(storageKey);

  // Reset initialization when the storage key changes (e.g. tenant switch)
  // so that filters are re-read from the new key.
  if (prevStorageKey.current !== storageKey) {
    prevStorageKey.current = storageKey;
    hasInitialized.current = false;
  }

  useEffect(() => {
    if (hasInitialized.current) return;

    const hasAnyQuery = Object.values(filters).some((v) => v !== undefined);
    if (hasAnyQuery) {
      localStorage.setItem(storageKey, JSON.stringify(filters));
      hasInitialized.current = true;
      return;
    }

    // No URL params — restore from localStorage or fall back to defaults
    let defaults = defaultFilters;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Object.keys(parsed).length > 0) {
          defaults = parsed;
        }
      }
    } catch {
      // ignore malformed localStorage
    }

    if (defaults) {
      setFilters((state) => ({ ...state, ...defaults }));
    }
    hasInitialized.current = true;
  }, [filters, setFilters, storageKey, defaultFilters]);

  // Persist filters to localStorage whenever they change.
  // storageKey is read from a ref so that a key change (tenant switch) does
  // not re-trigger this effect and write stale filters to the new key.
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;
  useEffect(() => {
    if (Object.values(filters).some((v) => v !== undefined)) {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(filters));
    }
  }, [filters]);

  return { filters, setFilters, navigateWithFilters };
}
